import {
  CallsMetricCard,
  CallsMetricGrid,
  CallsMetricHeader,
  CallsMetricSourceBadge,
  CallsMetricTitle,
  CallsMetricTrend,
  CallsMetricTrendPeriod,
  CallsMetricTrendValue,
  CallsMetricValue,
  CallsSectionBody,
  CallsSectionCard,
  CallsSectionHeader,
  CallsSectionSubtitle,
  CallsSectionSummary,
  CallsSectionTitle,
  CallsSourceButton,
  ExecutiveInfoTooltip,
  ExecutiveTooltipTrigger,
  SummaryHeroMetricTooltip,
} from "./styled"
import type { CallsSection as CallsSectionData } from "./types"

interface CallsSectionProps {
  calls: CallsSectionData
}

const callsTooltipText: Record<string, string> = {
  "calls-taken": "Total calls your customers placed to you through Nuve this month.",
  connected: "Calls where the forwarded leg reached your team (a person or voicemail picked up).",
  "answered-by-human": "Calls a person on your team actually answered. These are the captured leads.",
  "avg-talk-time": "Average length of answered calls.",
}

const getDisplayPercent = (value?: number) => Math.abs(value ?? 0)

export const CallsSection = ({ calls }: CallsSectionProps) => (
  <CallsSectionCard>
    <CallsSectionHeader>
      <div>
        <CallsSectionTitle>Calls</CallsSectionTitle>
        <CallsSectionSubtitle>Total calls your customers placed to you this month</CallsSectionSubtitle>
      </div>
      <CallsSourceButton>-&gt; via Twilio</CallsSourceButton>
    </CallsSectionHeader>
    <CallsSectionBody>
      <CallsMetricGrid>
        {calls.metrics.map(metric => {
          const tooltipId = `calls-section-${metric.id}`

          return (
            <CallsMetricCard key={metric.id}>
              <CallsMetricHeader>
                <CallsMetricTitle>
                  {metric.title}{" "}
                  {metric.sourceLabel ? <CallsMetricSourceBadge>{metric.sourceLabel}</CallsMetricSourceBadge> : null}
                </CallsMetricTitle>
                <SummaryHeroMetricTooltip>
                  <ExecutiveTooltipTrigger data-tooltip-id={tooltipId} tabIndex={0} />
                </SummaryHeroMetricTooltip>
              </CallsMetricHeader>
              <CallsMetricValue>{metric.value}</CallsMetricValue>
              <CallsMetricTrend $direction={metric.direction}>
                {metric.direction === "up" || metric.direction === "down" ? (
                  <svg
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                    focusable="false"
                    style={metric.direction === "down" ? { transform: "rotate(180deg)" } : undefined}
                  >
                    <path d="M8 13V3" />
                    <path d="M4.5 6.5L8 3l3.5 3.5" />
                  </svg>
                ) : null}
                <CallsMetricTrendValue $direction={metric.direction}>
                  {getDisplayPercent(metric.percent_change)}%
                </CallsMetricTrendValue>
                <CallsMetricTrendPeriod>past month</CallsMetricTrendPeriod>
              </CallsMetricTrend>
              <ExecutiveInfoTooltip id={tooltipId} place="top" clickable delayHide={150}>
                {callsTooltipText[metric.id]}
              </ExecutiveInfoTooltip>
            </CallsMetricCard>
          )
        })}
      </CallsMetricGrid>
      <CallsSectionSummary>{calls.summary}</CallsSectionSummary>
    </CallsSectionBody>
  </CallsSectionCard>
)
